import { useCallback, useMemo, useState } from 'react';

import type { LantmaterietProperty, Property } from '@ts-types/property';

import { searchProps } from '@vb-django/api/services/property';
import {
  getSubscriptionCheckout,
  getSubscriptionCouponDiscount
} from '@vb-django/api/services/subscription';
import { useLogin } from '@vb-django/components/login/context/LoginContext';
import { usePropsDataStore } from '@vb-django/components/minskog/store/PropsDataStore';

import { Button, Chip, Icon, Input, Text } from '@ui-component-library';

import {
  CheckoutConfig,
  CouponDiscount,
  OpenCheckoutOptions,
  PaymentFetchResponse,
  Product
} from '@components/Checkout/Checkout.types';
import { mapPropertiesData } from '@components/Checkout/config/utils/utils';
import { CheckoutProvider } from '@components/Checkout/context/CheckoutContext';
import { getStandardCheckoutStepKeys } from '@components/Checkout/utils/standardCheckoutStepKeys';

import { MultiPlusDiscountSummary } from './components/MultiPlusDiscountSummary';
import { SubscriptionCheckoutInner } from './components/SubscriptionCheckoutInner';
import { SubscriptionInfo } from './components/SubscriptionInfo';
import { handleSubscriptionPaymentError } from './subscriptionPaymentErrors';
import { confirmSubscriptionProductSelection } from './SubscriptionProductDialogConfirm';

type SortableProduct = Product & { designation?: string };

const sortLabel = (p: SortableProduct) => p.designation ?? p.name ?? '';

function svSortProducts(a: SortableProduct, b: SortableProduct) {
  return sortLabel(a).localeCompare(sortLabel(b), 'sv', { sensitivity: 'base' });
}

function mapOwnedProp(property: Product): Product {
  return {
    ...property,
    key: property.id,
    designation: property.name?.toLowerCase() ?? '',
    value: property.id,
    isOwned: true,
    isPlus: property.dfr
  };
}

function mapLmSearchHitToProduct(item: LantmaterietProperty): Product {
  return {
    id: item.registerenhet,
    type: 'PROP',
    name: item.beteckning,
    description: 'Fastighet från Lantmäteriets register',
    price: 0,
    designation: item.beteckning.toLowerCase(),
    lmSearchKey: item.objektidentitet,
    isOwned: false,
    dfr: false
  };
}

/** Same as legacy SubscriptionCheckoutContext: page `Window.react_data.owned_props` plus client store props. */
function mergeSubscriptionCheckoutProps(storeProps: Property[]): Property[] {
  const g = globalThis as typeof globalThis & {
    Window?: { react_data?: { owned_props?: Property[] } };
  };
  const ownedFromPage = g.Window?.react_data?.owned_props ?? [];
  const propOptions = [...ownedFromPage];
  storeProps.forEach((storeProp) => {
    const alreadyListed = propOptions.some((existing) => existing.ref === storeProp.ref);
    if (!alreadyListed) {
      propOptions.push(storeProp);
    }
  });
  return propOptions;
}

export default function SubscriptionCheckout({
  isOpen,
  onClose,
  openOptions,
  onConsumedOpenOptions
}: {
  isOpen?: boolean;
  onClose?: () => void;
  openOptions?: OpenCheckoutOptions | null;
  onConsumedOpenOptions?: () => void;
}) {
  const { isLogged } = useLogin() ?? { isLogged: false };
  const { props } = usePropsDataStore();
  const [isLoading, setIsLoading] = useState(false);

  /** Full merged list (page + store). Used for search owned-matches and LM de-dupe — same role as legacy `propOptions`. */
  const catalogProducts = useMemo(() => {
    if (!isOpen) {
      return [] as Product[];
    }
    const mergedProps = mergeSubscriptionCheckoutProps(props);
    const mappedProperties = mapPropertiesData(mergedProps);
    const formattedProducts = mappedProperties.map((property) => ({
      id: property.ref,
      type: property.type,
      name: property.designation,
      description: `${Math.round(property.forestArea)} hektar skogsmark`,
      price: property.plus_price ?? 0,
      forestArea: Math.round(property.forestArea),
      hasBadData: property.hasBadData,
      lmID: property.lmID,
      isShared: property.isShared,
      dfr: property.dfr
    }));
    return [...formattedProducts].sort(svSortProducts);
  }, [isOpen, props]);

  const catalogKey = useMemo(
    () => catalogProducts.map((product) => product.id).join('\t'),
    [catalogProducts]
  );

  /** Default picker list when not showing search results: empty for guests (legacy `defaultList`), full catalog when logged in. */
  const seedProducts = useMemo(
    () => (!isOpen || !isLogged ? [] : catalogProducts),
    [isOpen, isLogged, catalogProducts]
  );

  const [searchResults, setSearchResults] = useState<{
    baseKey: string;
    rows: Product[];
  } | null>(null);

  const initialProducts = useMemo(() => {
    if (!isOpen) {
      return [];
    }
    if (searchResults !== null && searchResults.baseKey === catalogKey) {
      return searchResults.rows;
    }
    return seedProducts;
  }, [isOpen, searchResults, catalogKey, seedProducts]);

  const handleSearch = useCallback(
    async (searchText: string) => {
      const baseKeyBeforeSearch = catalogProducts.map((product) => product.id).join('\t');

      if (searchText === '') {
        setSearchResults(null);
        return;
      }
      setIsLoading(true);
      try {
        const result = await searchProps(searchText);
        const rows = (result as { data?: LantmaterietProperty[] }).data ?? [];
        const searchQuery = searchText.toLowerCase();
        const ownedRefs = new Set(catalogProducts.map((property) => property.id));

        const ownedResults = catalogProducts
          .filter((property) => property.name?.toLowerCase().includes(searchQuery))
          .map(mapOwnedProp);

        const lmRows = rows
          .filter((item) => !ownedRefs.has(item.registerenhet))
          .map(mapLmSearchHitToProduct);

        const baseKeyAfterSearch = catalogProducts.map((product) => product.id).join('\t');
        if (baseKeyAfterSearch !== baseKeyBeforeSearch) {
          return;
        }

        setSearchResults({
          baseKey: baseKeyBeforeSearch,
          rows: [...ownedResults, ...lmRows].sort(svSortProducts)
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [catalogProducts]
  );

  const stepKeys = useMemo(() => getStandardCheckoutStepKeys(isLogged), [isLogged]);

  const config = useMemo<CheckoutConfig>(
    () => ({
      type: 'subscription',
      title: 'Bli Pluskund hos Treebula',
      summaryProductLabel: 'Treebula Plus (förnyas årligen)',
      selectProductLabel: 'Sök efter fastighet(er)',
      selectDialogTitle: 'Välj fastighet',
      badDataAdditionalInfo:
        'Tänk på att viss information kan vara mindre exakt för dessa fastigheter. Treebula Plus erbjuder ändå många insikter och funktioner, t.ex. virkespriser och marknadsinformation.',
      primaryActionLabel: 'Nästa steg',
      stepKeys,
      productContent: <SubscriptionInfo />,
      isOptionDisabled: (option) =>
        Boolean(option.dfr) || Boolean(option.isShared) || option.type !== 'PROP',
      renderOptionBadge: (option) => (
        <div className="flex flex-row items-center gap-1">
          <>
            <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
              Tillagd
            </Text>
            <Icon name="DotFilled" className="fill-[#737373]" size="xxsmall" />
            {option.dfr ? (
              <Chip
                label="Plus"
                className="bg-[#F6F5FE] fill-[#6856C3] text-[#6856C3]"
                icon="SparkleFilled"
              />
            ) : (
              <Chip label="Saknar Plus" className="bg-[#F4F5F5] text-[#30353A]" icon={null} />
            )}
          </>
        </div>
      ),
      searchField: (
        <div className="bg-white-50 sticky top-0 left-0 z-30 flex w-full flex-col gap-3 border-b border-solid border-[#E6E6E6] p-4">
          <Input
            variant="fluid"
            name="search"
            label="Ange en fastighetsbeteckning"
            placeholder="Sök t.ex. Ydre Sevedstorp 1:8"
            onChange={handleSearch}
            prefix={<Icon name="MagnifyingGlassSimple" size="medium" />}
          />
          <Text variant="text-sm/leading-normal/normal" className="mt-2 text-[#737373]">
            Visar fastigheter från Lantmäteriets register
          </Text>
        </div>
      ),
      summaryAdditionalContent: <MultiPlusDiscountSummary />,
      fetchCouponDiscount: async (code, products): Promise<CouponDiscount> => {
        const { ok, data } = await getSubscriptionCouponDiscount({
          refs: products.map((p) => p.id),
          package_id: undefined,
          forest_management_plan_id: undefined,
          coupon_code: code
        });
        if (!ok || !data) {
          throw new Error('Ogiltig rabattkod');
        }
        const discount: CouponDiscount = {
          ...(data as CouponDiscount),
          coupon_code: code
        };
        return discount;
      },
      fetchCheckout: (selectedProducts, couponDiscount, extraData) =>
        getSubscriptionCheckout({
          refs: selectedProducts.map((p) => p.id),
          properties: selectedProducts.map((p) => ({
            ref: p.id,
            designation: p.name
          })),
          coupon_code: couponDiscount?.coupon_code,
          extra_data: { ...(extraData ?? {}), create_if_missing: true }
        }) as Promise<PaymentFetchResponse>,
      handlePaymentError: handleSubscriptionPaymentError,
      secondaryActions: null,
      loading: isLoading,
      handleSelectedProductsOnClose: () => {
        setSearchResults(null);
      },
      confirmProductSelection: confirmSubscriptionProductSelection,
      selectedProductSummationText: 'hektar skogsmark',
      getSelectedProductSummationTotal: (products) =>
        products.reduce((acc, curr) => acc + Number(curr?.forestArea ?? 0), 0)
    }),
    [stepKeys, handleSearch, isLoading]
  );

  return (
    <CheckoutProvider
      totalSteps={stepKeys.length}
      config={config}
      initialProducts={initialProducts}
    >
      <SubscriptionCheckoutInner
        stepKeys={stepKeys}
        isOpen={isOpen}
        onClose={onClose}
        openOptions={openOptions}
        onConsumedOpenOptions={onConsumedOpenOptions}
      />
    </CheckoutProvider>
  );
}
